import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, Loader2, CheckCircle, AlertCircle, Flashlight } from 'lucide-react';
import { Button } from './ui/button';

export default function QRScanner({ onScan, onClose, isOpen }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
  }, []);

  const startScanner = useCallback(async (cameraId) => {
    if (!cameraId) return;
    
    try {
      setError(null);
      setScanning(true);

      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      }

      await html5QrCodeRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: 250,
        },
        async (decodedText) => {
          // Successfully scanned
          console.log('[QRScanner] Successfully scanned:', decodedText);
          setSuccess(decodedText);
          
          // Vibrate on success if supported
          if (navigator.vibrate) {
            navigator.vibrate(200);
          }

          // Stop scanner
          await stopScanner();

          // Call onScan callback
          if (onScan) {
            onScan(decodedText);
          }
        },
        (errorMessage) => {
          // Scan error - this is called frequently when no QR found, so we ignore it
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError(err.message || 'Failed to start camera');
      setScanning(false);
    }
  }, [onScan, stopScanner]);

  const initializeCameras = useCallback(async () => {
    try {
      // Request camera permission first
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (permErr) {
        console.error('Camera permission error:', permErr);
        setHasPermission(false);
        setError('Camera permission denied. Please allow camera access in your browser settings.');
        return;
      }
      
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length > 0) {
        setCameras(devices);
        setHasPermission(true);
        
        // Prefer back camera
        const backCamera = devices.find(
          d => d.label.toLowerCase().includes('back') || 
               d.label.toLowerCase().includes('rear') ||
               d.label.toLowerCase().includes('environment')
        );
        const cameraToUse = backCamera || devices[0];
        setSelectedCamera(cameraToUse.id);
        
        // Auto-start if open
        if (isOpen) {
          setTimeout(() => startScanner(cameraToUse.id), 500);
        }
      } else {
        setHasPermission(false);
        setError('No cameras found on this device');
      }
    } catch (err) {
      console.error('Error getting cameras:', err);
      setHasPermission(false);
      setError('Camera access failed. Please ensure camera permissions are granted and try again.');
    }
  }, [isOpen, startScanner]);

  useEffect(() => {
    if (isOpen) {
      initializeCameras();
    }
    
    return () => {
      stopScanner();
    };
  }, [isOpen, initializeCameras, stopScanner]);

  const handleClose = async () => {
    await stopScanner();
    setSuccess(null);
    setError(null);
    if (onClose) onClose();
  };

  const handleRetry = () => {
    setSuccess(null);
    setError(null);
    if (selectedCamera) {
      startScanner(selectedCamera);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h2 className="text-white font-semibold text-lg">Scan QR Code</h2>
        <button
          onClick={handleClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {hasPermission === false && (
          <div className="text-center text-white p-6">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <p className="text-lg mb-2">Camera Access Required</p>
            <p className="text-sm text-white/70 mb-4">
              Please allow camera access to scan QR codes
            </p>
            <Button onClick={initializeCameras} variant="outline" className="text-white border-white">
              Try Again
            </Button>
          </div>
        )}

        {hasPermission === true && !success && (
          <>
            <div 
              id="qr-reader" 
              ref={scannerRef}
              className="w-full max-w-sm aspect-square rounded-xl overflow-hidden bg-black"
            />
            <p className="text-white/70 text-sm mt-4 text-center">
              Position the QR code within the frame
            </p>
            
            {/* Camera Selector */}
            {cameras.length > 1 && (
              <div className="mt-4">
                <select
                  value={selectedCamera || ''}
                  onChange={async (e) => {
                    await stopScanner();
                    setSelectedCamera(e.target.value);
                    setTimeout(() => startScanner(e.target.value), 300);
                  }}
                  className="bg-white/10 text-white border border-white/20 rounded-lg px-4 py-2 text-sm"
                >
                  {cameras.map((camera) => (
                    <option key={camera.id} value={camera.id}>
                      {camera.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {success && (
          <div className="text-center text-white p-6">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
            <p className="text-lg mb-2">QR Code Scanned!</p>
            <p className="text-sm text-white/70 font-mono bg-white/10 p-3 rounded-lg mb-4 break-all">
              {success}
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleRetry} variant="outline" className="text-white border-white">
                Scan Another
              </Button>
              <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700">
                Done
              </Button>
            </div>
          </div>
        )}

        {error && !success && (
          <div className="text-center text-white p-6">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <p className="text-lg mb-2">Scanner Error</p>
            <p className="text-sm text-white/70 mb-4">{error}</p>
            <Button onClick={handleRetry} variant="outline" className="text-white border-white">
              Try Again
            </Button>
          </div>
        )}

        {hasPermission === null && (
          <div className="text-center text-white">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" />
            <p>Initializing camera...</p>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="p-4 bg-black/50 text-center">
        <p className="text-white/50 text-xs">
          Point your camera at a checkpoint QR code
        </p>
      </div>
    </div>
  );
}
